import { useEffect, useRef } from 'react';

const team = [
  {
    name: 'Alexander Petrov',
    role: 'CEO & Co-founder',
    bio: 'Visionary leader with 12 years in mobile product development. Passionate about building technology that genuinely helps people.',
    initials: 'AP',
  },
  {
    name: 'Maria Ivanova',
    role: 'Lead iOS Engineer',
    bio: 'Swift and Objective-C expert with a track record of shipping performance-critical apps used by millions worldwide.',
    initials: 'MI',
  },
  {
    name: 'Dimitar Stoyanov',
    role: 'Security Architect',
    bio: 'Specialist in network security and encryption protocols. Ensures every line of security-critical code meets the highest standards.',
    initials: 'DS',
  },
  {
    name: 'Elena Todorova',
    role: 'UX & Product Design',
    bio: 'Human-centered designer who transforms complex features into beautifully simple experiences. Every pixel has a purpose.',
    initials: 'ET',
  },
  {
    name: 'Nikolay Georgiev',
    role: 'Product Manager',
    bio: 'Bridges strategy and execution. Deeply data-driven and relentlessly focused on delivering value to users at every release.',
    initials: 'NG',
  },
  {
    name: 'Sofia Marinova',
    role: 'Backend Engineer',
    bio: 'Architect of the scalable cloud infrastructure that powers our apps globally — quietly making millions of connections fast and secure.',
    initials: 'SM',
  },
];

export default function TeamSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-20">
          <div
            data-animate
            className="inline-block text-emerald-600 text-xs font-semibold tracking-widest uppercase mb-5 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif" }}
          >
            Our Team
          </div>
          <h2
            data-animate
            className="text-zinc-900 font-bold mb-5 leading-tight transition-all duration-700"
            style={{
              opacity: 0,
              transform: 'translateY(20px)',
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              transitionDelay: '120ms',
            }}
          >
            The People Behind the Products
          </h2>
          <p
            data-animate
            className="text-zinc-500 max-w-xl mx-auto leading-relaxed transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif", transitionDelay: '240ms' }}
          >
            A tight-knit group of engineers, designers, and strategists united by a shared obsession with quality.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {team.map((member, i) => (
            <div
              key={member.name}
              data-animate
              className="bg-white rounded-2xl border border-zinc-100 group transition-all duration-700 hover:border-emerald-200 p-8"
              style={{ opacity: 0, transform: 'translateY(28px)', transitionDelay: `${300 + i * 100}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 mb-5 flex-shrink-0">
                <span
                  className="text-emerald-600 font-bold text-base"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {member.initials}
                </span>
              </div>
              <p className="text-emerald-600 text-xs font-semibold tracking-widest uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                {member.role}
              </p>
              <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {member.bio}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
